import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert, Box, IconButton, Paper, Slide, Slider, Snackbar, TextField, Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowLeftIcon from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import UndoIcon from '@mui/icons-material/Undo';
import AcUnitIcon from '@mui/icons-material/AcUnit';
import WhatshotIcon from '@mui/icons-material/Whatshot';
import AirIcon from '@mui/icons-material/Air';
import WaterDropIcon from '@mui/icons-material/WaterDrop';
import TuneIcon from '@mui/icons-material/Tune';
import { useOrientation } from '../../hooks/useOrientation';
import { SHC_BOTTOM_NAV_CLEARANCE } from '../../constants/bottomNav';
import { useCloudModalAutoClose } from '../../hooks/useCloudModalAutoClose';
import { useOverlayBackButton } from '../../hooks/useOverlayBackButton';
import { SamsungDevice, samsungControl, samsungRefreshDevice } from '../../services/samsungApi';
import { mapaSamsung } from '../../services/samsungState';
import { eventBus } from '../../services/EventBus';
import { requirePinIfNeeded } from '../../services/pinGate';
import PowerButton from '../shared/PowerButton';
import SamsungActivityChart from './SamsungActivityChart';
import {
  AC_MODES_UI, availableFanModes, stHvacModeValue, uiHvacModeValue,
} from './samsungModeUtils';
import { getDemoRestrictionSnackbarProps } from '../../constants/demoRestrictions';

const ACCENT = '#2391FF';
const SURFACE = 'rgba(255,255,255,0.04)';
const SURFACE_BORDER = 'rgba(255,255,255,0.08)';
const PRESS_TRANSITION = 'transform 140ms cubic-bezier(0.2, 0.9, 0.3, 1), background-color 160ms ease, border-color 160ms ease, box-shadow 180ms ease, color 160ms ease, opacity 90ms ease';
/** Espera tras el último ± antes de enviar el valor absoluto a ST. */
const SETPOINT_DEBOUNCE_MS = 300;
/** Rango real Samsung ducted (custom.thermostatSetpointControl). */
const SETPOINT_MIN = 18;
const SETPOINT_MAX = 30;
/** Sync AC→PWA en idle. TV no hace loop (ST volume está congelado y quema cupo). */
const MODAL_SYNC_INTERVAL_MS = 6000;
/** Tras error de refresh, backoff largo (evita martillar ST con 502). */
const MODAL_SYNC_RETRY_MS = 12000;
/** Tras un control AC, pausar sync. */
const MODAL_SYNC_PAUSE_AFTER_CONTROL_MS = 5000;

const HVAC_META: Record<string, { color: string; icon: React.ReactNode }> = {
  auto: { color: '#90A4AE', icon: <TuneIcon sx={{ fontSize: 18 }} /> },
  cool: { color: '#42A5F5', icon: <AcUnitIcon sx={{ fontSize: 18 }} /> },
  heat: { color: '#FF8A65', icon: <WhatshotIcon sx={{ fontSize: 18 }} /> },
  fan: { color: '#81C784', icon: <AirIcon sx={{ fontSize: 18 }} /> },
  dry: { color: '#4DD0E1', icon: <WaterDropIcon sx={{ fontSize: 18 }} /> },
};

interface Props {
  open: boolean;
  device: SamsungDevice | null;
  onClose: () => void;
  onUpdated?: (d: SamsungDevice) => void;
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <Typography sx={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'rgba(255,255,255,0.45)',
      mb: 0.85,
    }}>
      {children}
    </Typography>
  );
}

/** Chip de estado para la barra de título (Online / Offline / Sin enlace ST). */
function StatusChip({ color, label }: { color: string; label: string }) {
  return (
    <Box sx={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 0.55,
      px: 0.9,
      py: 0.3,
      borderRadius: 999,
      bgcolor: `${color}18`,
      border: `1px solid ${color}55`,
      maxWidth: 118,
      flexShrink: 0,
    }}>
      <Box sx={{
        width: 6,
        height: 6,
        borderRadius: '50%',
        bgcolor: color,
        boxShadow: `0 0 8px ${color}`,
        flexShrink: 0,
        animation: 'samsungPulse 2.2s ease-in-out infinite',
        '@keyframes samsungPulse': {
          '0%, 100%': { opacity: 1 },
          '50%': { opacity: 0.45 },
        },
      }} />
      <Typography sx={{
        fontSize: 10,
        fontWeight: 600,
        color,
        letterSpacing: 0.15,
        lineHeight: 1.1,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
      }}>
        {label}
      </Typography>
    </Box>
  );
}

function ChoiceChip({
  selected,
  disabled,
  onClick,
  color = ACCENT,
  children,
  minHeight = 40,
}: {
  selected: boolean;
  disabled?: boolean;
  onClick: () => void;
  color?: string;
  children: React.ReactNode;
  minHeight?: number;
}) {
  return (
    <Box
      component="button"
      type="button"
      disabled={disabled}
      onClick={onClick}
      sx={{
        appearance: 'none',
        border: selected ? `1px solid ${color}` : `1px solid ${SURFACE_BORDER}`,
        bgcolor: selected ? `${color}22` : SURFACE,
        color: selected ? '#fff' : 'rgba(255,255,255,0.72)',
        borderRadius: '12px',
        minHeight,
        px: 1,
        py: 0.75,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 0.35,
        cursor: disabled ? 'not-allowed' : 'pointer',
        opacity: disabled ? 0.4 : 1,
        transition: PRESS_TRANSITION,
        boxShadow: selected ? `0 6px 18px ${color}28, inset 0 0 0 1px ${color}33` : 'none',
        WebkitTapHighlightColor: 'transparent',
        '&:hover:not(:disabled)': {
          borderColor: selected ? color : 'rgba(255,255,255,0.18)',
          bgcolor: selected ? `${color}2e` : 'rgba(255,255,255,0.07)',
        },
        '&:active:not(:disabled)': {
          transform: 'scale(0.94)',
        },
      }}
    >
      {children}
    </Box>
  );
}

function RoundControl({
  disabled,
  onClick,
  active,
  ariaLabel,
  size = 48,
  children,
}: {
  disabled?: boolean;
  onClick: () => void;
  active?: boolean;
  ariaLabel: string;
  size?: number;
  children: React.ReactNode;
}) {
  return (
    <IconButton
      aria-label={ariaLabel}
      disabled={disabled}
      onClick={onClick}
      sx={{
        width: size,
        height: size,
        color: active ? ACCENT : '#fff',
        bgcolor: active ? 'rgba(35,145,255,0.18)' : SURFACE,
        border: active ? `1px solid ${ACCENT}` : `1px solid ${SURFACE_BORDER}`,
        transition: PRESS_TRANSITION,
        WebkitTapHighlightColor: 'transparent',
        '&:hover:not(.Mui-disabled)': {
          bgcolor: active ? 'rgba(35,145,255,0.28)' : 'rgba(255,255,255,0.08)',
          borderColor: ACCENT,
        },
        '&:active:not(.Mui-disabled)': {
          transform: 'scale(0.9)',
        },
        '&.Mui-disabled': {
          color: '#666',
          borderColor: '#2a2a2a',
          bgcolor: '#121212',
        },
      }}
    >
      {children}
    </IconButton>
  );
}

const SamsungDeviceModal: React.FC<Props> = ({ open, device, onClose, onUpdated }) => {
  const { t } = useTranslation();
  const isHorizontal = useOrientation();
  const [powerBusy, setPowerBusy] = useState(false);
  const [configBusy, setConfigBusy] = useState(false);
  const [ambientBusy, setAmbientBusy] = useState(false);
  const [localSetpoint, setLocalSetpoint] = useState<number | null>(null);
  const [editingSetpoint, setEditingSetpoint] = useState(false);
  const [setpointDraft, setSetpointDraft] = useState('');
  const [localHvac, setLocalHvac] = useState<string | null>(null);
  const [localFan, setLocalFan] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [permissionSnackbar, setPermissionSnackbar] = useState<{ open: boolean; message: string }>({
    open: false,
    message: '',
  });
  const [isClosing, setIsClosing] = useState(false);
  const [pressedKey, setPressedKey] = useState<string | null>(null);
  const [volumeDraft, setVolumeDraft] = useState('0');
  const [editingVolume, setEditingVolume] = useState(false);
  /** Volumen optimista de sesión (ST cloud no reporta audioVolume en vivo). */
  const [localVolume, setLocalVolume] = useState<number | null>(null);
  const [mapTick, setMapTick] = useState(0);
  const setpointInputRef = useRef<HTMLInputElement | null>(null);
  const swipeStartRef = useRef<{ x: number; y: number } | null>(null);
  const pressTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setpointSeqRef = useRef(0);
  const setpointDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingSetpointRef = useRef<number | null>(null);
  const deviceIdRef = useRef<number | null>(null);
  const syncPausedUntilRef = useRef(0);
  const onUpdatedRef = useRef(onUpdated);
  onUpdatedRef.current = onUpdated;

  useEffect(() => {
    if (!open) return undefined;
    return eventBus.on('samsung:updated', () => setMapTick((t) => t + 1));
  }, [open]);

  const live = device
    ? ((mapTick >= 0 && mapaSamsung[device.id]) || device)
    : null;
  deviceIdRef.current = live?.id ?? null;
  const displayVolume = localVolume != null
    ? localVolume
    : (live?.volume != null ? Number(live.volume) : 0);

  useEffect(() => {
    if (!editingVolume) setVolumeDraft(String(displayVolume));
  }, [live?.id, displayVolume, editingVolume]);

  // Soltar override solo cuando el mapa ya refleja el valor seteado.
  useEffect(() => {
    if (localVolume == null) return;
    if (live?.volume != null && Number(live.volume) === localVolume) {
      setLocalVolume(null);
    }
  }, [live?.volume, localVolume]);

  const pauseModalSync = useCallback(() => {
    syncPausedUntilRef.current = Date.now() + MODAL_SYNC_PAUSE_AFTER_CONTROL_MS;
  }, []);

  const flushSetpoint = useCallback(async (next: number) => {
    const id = deviceIdRef.current;
    if (id == null) return;
    pauseModalSync();
    const seq = ++setpointSeqRef.current;
    try {
      const res = await samsungControl(id, 'setpoint', next);
      if (seq !== setpointSeqRef.current) return;
      if (res?.device) onUpdatedRef.current?.(res.device);
    } catch (e: any) {
      if (seq !== setpointSeqRef.current) return;
      setLocalSetpoint(null);
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
    }
  }, [pauseModalSync, t]);

  const scheduleSetpointSend = useCallback((next: number) => {
    pauseModalSync();
    pendingSetpointRef.current = next;
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
    setpointDebounceRef.current = setTimeout(() => {
      setpointDebounceRef.current = null;
      const val = pendingSetpointRef.current;
      pendingSetpointRef.current = null;
      if (val != null) void flushSetpoint(val);
    }, SETPOINT_DEBOUNCE_MS);
  }, [flushSetpoint, pauseModalSync]);

  const flushPendingSetpointNow = useCallback(() => {
    if (setpointDebounceRef.current) {
      clearTimeout(setpointDebounceRef.current);
      setpointDebounceRef.current = null;
    }
    const val = pendingSetpointRef.current;
    pendingSetpointRef.current = null;
    if (val != null) void flushSetpoint(val);
  }, [flushSetpoint]);

  useEffect(() => {
    if (open) {
      setIsClosing(false);
      setError(null);
      setPressedKey(null);
      setLocalSetpoint(null);
      setEditingSetpoint(false);
      setSetpointDraft('');
      setLocalHvac(null);
      setLocalFan(null);
      setPowerBusy(false);
      setConfigBusy(false);
      setAmbientBusy(false);
      setLocalVolume(null);
      setEditingVolume(false);
      pendingSetpointRef.current = null;
      if (setpointDebounceRef.current) {
        clearTimeout(setpointDebounceRef.current);
        setpointDebounceRef.current = null;
      }
    }
  }, [open, device?.id]);

  // TV: un solo refresh al abrir (power/online). No loop: quema cupo ST y el volume cloud está congelado.
  // AC: sync periódico de termostato.
  useEffect(() => {
    if (!open || !device?.id) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | null = null;
    let first = true;
    const isTv = device.device_kind === 'tv';

    const schedule = (ms: number) => {
      timer = setTimeout(() => { void tick(); }, ms);
    };

    const tick = async () => {
      if (cancelled) return;
      const pauseLeft = syncPausedUntilRef.current - Date.now();
      if (pauseLeft > 0) {
        schedule(pauseLeft);
        return;
      }
      let ok = false;
      try {
        const d = await samsungRefreshDevice(device.id);
        if (!cancelled && d) onUpdatedRef.current?.(d);
        ok = true;
        if (first) {
          first = false;
          setError(null);
        }
      } catch (e: any) {
        if (!cancelled && first) {
          first = false;
          const detail = e?.response?.data?.detail;
          const msg = typeof detail === 'string' ? detail : detail?.message;
          if (msg) setError(msg);
        }
      }
      // TV: no re-programar. AC: seguir en idle.
      if (!cancelled && !isTv) schedule(ok ? MODAL_SYNC_INTERVAL_MS : MODAL_SYNC_RETRY_MS);
    };

    void tick();
    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [open, device?.id, device?.device_kind]);

  // Cuando llega el valor real desde ST, soltar overrides optimistas.
  useEffect(() => {
    if (localSetpoint == null) return;
    if (pendingSetpointRef.current != null) return; // aún editando ±
    if (live?.setpoint != null && Math.round(Number(live.setpoint)) === Math.round(localSetpoint)) {
      setLocalSetpoint(null);
    }
  }, [live?.setpoint, localSetpoint]);

  useEffect(() => {
    if (localHvac == null) return;
    if (String(live?.hvac_mode || '').toLowerCase() === String(localHvac).toLowerCase()) {
      setLocalHvac(null);
    }
  }, [live?.hvac_mode, localHvac]);

  useEffect(() => {
    if (localFan == null) return;
    if (String(live?.fan_mode || '').toLowerCase() === String(localFan).toLowerCase()) {
      setLocalFan(null);
    }
  }, [live?.fan_mode, localFan]);

  useEffect(() => () => {
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    if (setpointDebounceRef.current) clearTimeout(setpointDebounceRef.current);
  }, []);

  const beginClose = useCallback(() => {
    flushPendingSetpointNow();
    setIsClosing(true);
    setTimeout(() => {
      onClose();
      setIsClosing(false);
    }, 280);
  }, [onClose, flushPendingSetpointNow]);

  // Mismo patrón que overlays Sonos: botón Back del teléfono cierra el modal.
  const requestClose = useOverlayBackButton(open, beginClose, 'samsung-device-modal');
  const { noteActivity } = useCloudModalAutoClose(open, requestClose, live?.id ?? device?.id);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    swipeStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const handleTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!swipeStartRef.current) return;
    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - swipeStartRef.current.x;
    const deltaY = Math.abs(touch.clientY - swipeStartRef.current.y);
    if (deltaX > 80 && deltaX > deltaY) requestClose();
    swipeStartRef.current = null;
  }, [requestClose]);

  useEffect(() => {
    if (!live) return;
    const hvac = uiHvacModeValue(localHvac ?? live.hvac_mode);
    const unavailable = hvac === 'fan';
    const offlineNow = !live.online;
    const disabled = !live.can_control
      || (offlineNow && live.device_kind === 'ac')
      || unavailable;
    if (unavailable || disabled) setEditingSetpoint(false);
  }, [live, localHvac]);

  useEffect(() => {
    if (!editingSetpoint) return undefined;
    const focusTimer = setTimeout(() => setpointInputRef.current?.focus(), 0);
    return () => clearTimeout(focusTimer);
  }, [editingSetpoint]);

  if (!open || !live) return null;

  const can = Boolean(live.can_control);
  const offline = !live.online;
  const health = String(live.health_status || '').toUpperCase();
  const tvCloudSleepy = live.device_kind === 'tv'
    && live.control_mode !== 'lan'
    && ['OFFLINE', 'UNHEALTHY', 'INACTIVE'].includes(health);
  const hvacUi = uiHvacModeValue(localHvac ?? live.hvac_mode);
  const fanModes = availableFanModes(localHvac ?? live.hvac_mode);
  const fanValue = hvacUi === 'dry'
    ? 'auto'
    : ((localFan ?? live.fan_mode) || '');
  const blockByOffline = offline && live.device_kind === 'ac';
  const modeColor = HVAC_META[hvacUi]?.color || ACCENT;
  const powerOn = Boolean(live.power);

  /** Tocar ambiente → forzar lectura ST (el sensor tarda más que setpoint/modo). */
  const refreshAmbient = async () => {
    if (ambientBusy || live.device_kind !== 'ac') return;
    setError(null);
    setAmbientBusy(true);
    try {
      const d = await samsungRefreshDevice(live.id);
      if (d) onUpdated?.(d);
    } catch (e: any) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === 'string' ? detail : detail?.message;
      setError(msg || e?.message || t('samsung.modal.ambientRefreshError'));
    } finally {
      setAmbientBusy(false);
    }
  };

  const executeRun = async (action: string, value?: unknown) => {
    setError(null);

    // Power: spinner solo en el botón (igual que la tarjeta).
    if (action === 'power') {
      pauseModalSync();
      setPowerBusy(true);
      try {
        const res = await samsungControl(live.id, action, value);
        if (res?.device) onUpdated?.(res.device);
      } catch (e: any) {
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
      } finally {
        setPowerBusy(false);
      }
      return;
    }

    // Setpoint: UI optimista en cada ±; un solo envío del valor absoluto al dejar de tocar.
    if (action === 'setpoint') {
      const next = Math.min(SETPOINT_MAX, Math.max(SETPOINT_MIN, Number(value)));
      if (!Number.isFinite(next)) return;
      setLocalSetpoint(next);
      scheduleSetpointSend(next);
      return;
    }

    // Modo / ventilador: grisado hasta confirmar; selección optimista debajo del gris.
    if (action === 'hvac_mode' || action === 'fan_mode') {
      if (action === 'hvac_mode') {
        setLocalHvac(String(value));
        if (String(value).toLowerCase() === 'dry') setLocalFan('auto');
        // En ventilador no hay objetivo: cancelar ± pendientes.
        if (uiHvacModeValue(String(value)) === 'fan') {
          if (setpointDebounceRef.current) {
            clearTimeout(setpointDebounceRef.current);
            setpointDebounceRef.current = null;
          }
          pendingSetpointRef.current = null;
          setLocalSetpoint(null);
        }
      } else {
        setLocalFan(String(value));
      }
      pauseModalSync();
      setConfigBusy(true);
      try {
        const res = await samsungControl(live.id, action, value);
        if (res?.device) onUpdated?.(res.device);
        if (action === 'hvac_mode' && String(value).toLowerCase() === 'dry') {
          const after = res?.device || live;
          if (after.fan_mode && String(after.fan_mode).toLowerCase() !== 'auto') {
            pauseModalSync();
            const fanRes = await samsungControl(live.id, 'fan_mode', 'auto');
            if (fanRes?.device) onUpdated?.(fanRes.device);
          }
        }
      } catch (e: any) {
        setLocalHvac(null);
        setLocalFan(null);
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
      } finally {
        setConfigBusy(false);
      }
      return;
    }

    // Volumen / mute TV: anclar UI al valor comandado (ST no actualiza audioVolume).
    if (action === 'volume' || action === 'mute') {
      if (action === 'volume') {
        const next = Math.max(0, Math.min(100, Number(value)));
        if (!Number.isFinite(next)) return;
        setLocalVolume(next);
        setVolumeDraft(String(next));
        try {
          const res = await samsungControl(live.id, action, next);
          if (res?.device) onUpdated?.(res.device);
        } catch (e: any) {
          setLocalVolume(null);
          const d = e?.response?.data?.detail;
          setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
        }
        return;
      }
      try {
        const res = await samsungControl(live.id, action, value);
        if (res?.device) onUpdated?.(res.device);
      } catch (e: any) {
        const d = e?.response?.data?.detail;
        setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
      }
      return;
    }

    // Resto (TV teclas, mute, etc.): sin spinner de power ni grisado global.
    try {
      const res = await samsungControl(live.id, action, value);
      if (res?.device) onUpdated?.(res.device);
    } catch (e: any) {
      const d = e?.response?.data?.detail;
      setError(typeof d === 'string' ? d : (d?.message || e?.message || t('samsung.modal.controlError')));
    }
  };

  const run = async (action: string, value?: unknown) => {
    if (!can) {
      setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') });
      return;
    }
    if (blockByOffline) {
      setError(t('samsung.modal.offlineError'));
      return;
    }
    if (!requirePinIfNeeded(live.pin_required, live.id, () => {
      void executeRun(action, value);
    })) return;
    await executeRun(action, value);
  };

  const flashKey = (key: string) => {
    setPressedKey(key);
    if (pressTimerRef.current) clearTimeout(pressTimerRef.current);
    pressTimerRef.current = setTimeout(() => setPressedKey(null), 160);
  };

  const runKey = (key: string) => {
    flashKey(key);
    void run('key', key);
  };

  const statusColor = offline ? '#e57373' : (tvCloudSleepy ? '#e5a54b' : '#66bb6a');
  const statusLabel = offline
    ? t('devices.offline')
    : (tvCloudSleepy ? t('samsung.modal.noLinkSt') : t('devices.online'));
  const sleepyWarning = tvCloudSleepy && !offline
    ? t('samsung.modal.sleepyWarning')
    : null;

  const displaySetpoint = localSetpoint != null
    ? localSetpoint
    : (live.setpoint != null ? Math.round(Number(live.setpoint)) : null);
  // En modo ventilador (wind) el termostato físico no tiene objetivo.
  const setpointUnavailable = hvacUi === 'fan';
  // Sin !can en disabled: el tap debe llegar a run() para mostrar el Snackbar de permisos.
  const setpointDisabled = blockByOffline || setpointUnavailable;

  const beginEditSetpoint = () => {
    if (!can) {
      setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') });
      return;
    }
    if (setpointDisabled) return;
    setSetpointDraft(displaySetpoint != null ? String(displaySetpoint) : '');
    setEditingSetpoint(true);
  };

  const commitEditSetpoint = () => {
    if (!editingSetpoint) return;
    setEditingSetpoint(false);
    const raw = setpointDraft.replace(/[^\d]/g, '');
    if (raw === '') return;
    const n = Math.min(SETPOINT_MAX, Math.max(SETPOINT_MIN, parseInt(raw, 10)));
    if (!Number.isFinite(n)) return;
    if (displaySetpoint != null && n === displaySetpoint) return;
    void run('setpoint', n);
  };

  const cancelEditSetpoint = () => {
    setEditingSetpoint(false);
    setSetpointDraft('');
  };
  const modeFanDisabled = configBusy || blockByOffline;

  const header = (
    <Box sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      px: 1,
      py: 0.65,
      borderBottom: `1px solid ${SURFACE_BORDER}`,
      background: 'linear-gradient(180deg, #1c1c1c 0%, #141414 100%)',
      minHeight: 40,
      flexShrink: 0,
      gap: 0.5,
    }}>
      <Box sx={{ display: 'flex', alignItems: 'center', minWidth: 0, flexShrink: 1 }}>
        <StatusChip color={statusColor} label={statusLabel} />
      </Box>
      <Box sx={{ flex: 1, textAlign: 'center', px: 0.5, minWidth: 0 }}>
        <Typography sx={{
          color: 'white',
          fontWeight: 700,
          fontSize: '0.82rem',
          lineHeight: 1.15,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        }}>
          {live.nombre}
        </Typography>
        <Typography sx={{
          fontSize: 10,
          color: 'rgba(255,255,255,0.4)',
          letterSpacing: '0.06em',
          textTransform: 'uppercase',
          mt: 0.15,
        }}>
          {live.device_kind === 'tv' ? t('samsung.modal.deviceKindTv') : t('samsung.modal.deviceKindAc')}
        </Typography>
      </Box>
      <IconButton onClick={requestClose} sx={{
        color: 'white',
        p: 0.55,
        flexShrink: 0,
        transition: PRESS_TRANSITION,
        '&:active': { transform: 'scale(0.9)' },
      }}>
        <CloseIcon sx={{ fontSize: 18 }} />
      </IconButton>
    </Box>
  );

  const powerRow = (
    <Box sx={{
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'space-between',
      gap: 1.5,
      px: 1.25,
      py: 1,
      borderRadius: '16px',
      bgcolor: SURFACE,
      border: `1px solid ${SURFACE_BORDER}`,
      backgroundImage: powerOn
        ? `radial-gradient(120% 90% at 15% 50%, ${modeColor}22 0%, transparent 55%)`
        : 'none',
      transition: 'background-image 280ms ease',
    }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 12, color: 'rgba(255,255,255,0.5)', fontWeight: 600 }}>
          {t('samsung.modal.power')}
        </Typography>
        <Typography sx={{
          fontSize: 15,
          fontWeight: 700,
          color: powerOn ? '#fff' : 'rgba(255,255,255,0.55)',
          letterSpacing: 0.2,
        }}>
          {powerOn ? t('devices.on') : t('devices.off')}
        </Typography>
      </Box>
      <PowerButton
        isOn={powerOn}
        isUnknown={offline && live.device_kind === 'ac'}
        isLoading={powerBusy}
        onClick={() => { void run('power', !live.power); }}
        deviceId={live.id}
        timerPosition="left"
      />
    </Box>
  );

  const acBody = (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
      {/* Hero clima (~+10% vs tamaños previos) */}
      <Box sx={{
        position: 'relative',
        borderRadius: '18px',
        px: 1.65,
        pt: 1.2,
        pb: 1.25,
        border: `1px solid ${SURFACE_BORDER}`,
        background: `
          radial-gradient(80% 70% at 50% 0%, ${modeColor}26 0%, transparent 60%),
          linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.2) 100%)
        `,
      }}>
        <Box sx={{
          position: 'absolute',
          inset: 0,
          borderRadius: '18px',
          overflow: 'hidden',
          background: `radial-gradient(circle at 70% 80%, ${modeColor}10 0%, transparent 40%)`,
          pointerEvents: 'none',
        }} />

        <Box sx={{
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-evenly',
          gap: 0.85,
        }}>
          {/* Ambiente: tocar valor/label fuerza refresh desde ST */}
          <Box
            role="button"
            tabIndex={0}
            aria-label={t('samsung.modal.refreshAmbient')}
            onClick={() => { void refreshAmbient(); }}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                void refreshAmbient();
              }
            }}
            sx={{
              flex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 0,
              cursor: ambientBusy ? 'wait' : 'pointer',
              borderRadius: '12px',
              px: 0.4,
              WebkitTapHighlightColor: 'transparent',
              transition: 'opacity 180ms ease, transform 120ms ease',
              opacity: ambientBusy ? 0.55 : 1,
              '&:active': ambientBusy ? undefined : { transform: 'scale(0.97)' },
            }}
          >
            <Typography sx={{
              fontSize: 11,
              color: 'rgba(255,255,255,0.45)',
              fontWeight: 600,
              letterSpacing: '0.06em',
              textTransform: 'uppercase',
              mb: 0.3,
              lineHeight: 1.15,
              textAlign: 'center',
            }}>
              {ambientBusy ? t('samsung.modal.ambientBusy') : t('samsung.modal.ambient')}
            </Typography>
            <Box sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 40,
              overflow: 'visible',
            }}>
              <Typography sx={{
                fontSize: 33,
                fontWeight: 720,
                lineHeight: 1.12,
                letterSpacing: '-0.03em',
                color: '#fff',
                textShadow: `0 0 28px ${modeColor}55`,
                transition: 'text-shadow 280ms ease, color 280ms ease',
              }}>
                {live.temperature != null ? `${Number(live.temperature).toFixed(1)}°` : '—'}
              </Typography>
            </Box>
          </Box>

          {/* Objetivo: label centrado solo sobre el valor; ± a los costados */}
          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 0.55,
            minWidth: 0,
            opacity: setpointUnavailable ? 0.38 : 1,
            transition: 'opacity 180ms ease',
          }}>
            <RoundControl
              ariaLabel={t('samsung.modal.setpointDown')}
              disabled={setpointDisabled}
              onClick={() => {
                if (editingSetpoint) cancelEditSetpoint();
                void run('setpoint', (displaySetpoint ?? 22) - 1);
              }}
              size={37}
            >
              <RemoveIcon sx={{ fontSize: 22 }} />
            </RoundControl>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              minWidth: 53,
            }}>
              <Typography sx={{
                fontSize: 11,
                color: 'rgba(255,255,255,0.45)',
                fontWeight: 600,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                mb: 0.3,
                lineHeight: 1.15,
                textAlign: 'center',
              }}>
                {t('samsung.modal.target')}
              </Typography>
              <Box sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                minHeight: 40,
                minWidth: 56,
                overflow: 'visible',
              }}>
                {setpointUnavailable ? (
                  <Typography sx={{
                    textAlign: 'center',
                    fontSize: 31,
                    fontWeight: 750,
                    letterSpacing: '-0.02em',
                    lineHeight: 1.12,
                    color: 'rgba(255,255,255,0.35)',
                  }}>
                    —
                  </Typography>
                ) : editingSetpoint ? (
                  <TextField
                    inputRef={setpointInputRef}
                    value={setpointDraft}
                    onChange={(e) => {
                      const v = e.target.value.replace(/[^\d]/g, '').slice(0, 2);
                      setSetpointDraft(v);
                    }}
                    onBlur={() => { commitEditSetpoint(); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        (e.target as HTMLInputElement).blur();
                      } else if (e.key === 'Escape') {
                        e.preventDefault();
                        cancelEditSetpoint();
                      }
                    }}
                    variant="standard"
                    inputProps={{
                      inputMode: 'numeric',
                      'aria-label': t('samsung.modal.targetTemp'),
                      style: {
                        textAlign: 'center',
                        fontSize: 31,
                        fontWeight: 750,
                        letterSpacing: '-0.02em',
                        color: modeColor,
                        padding: 0,
                        width: 48,
                      },
                    }}
                    sx={{
                      width: 56,
                      '& .MuiInput-underline:before': { borderBottomColor: `${modeColor}66` },
                      '& .MuiInput-underline:after': { borderBottomColor: modeColor },
                      '& .MuiInput-underline:hover:not(.Mui-disabled):before': {
                        borderBottomColor: modeColor,
                      },
                    }}
                  />
                ) : (
                  <Typography
                    role="button"
                    tabIndex={setpointDisabled ? -1 : 0}
                    aria-label={t('samsung.modal.editTargetTemp')}
                    onClick={() => { beginEditSetpoint(); }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        beginEditSetpoint();
                      }
                    }}
                    sx={{
                      textAlign: 'center',
                      fontSize: 31,
                      fontWeight: 750,
                      letterSpacing: '-0.02em',
                      lineHeight: 1.12,
                      color: modeColor,
                      cursor: setpointDisabled ? 'default' : 'pointer',
                      WebkitTapHighlightColor: 'transparent',
                      borderRadius: '8px',
                      px: 0.25,
                      '&:active': setpointDisabled ? undefined : { opacity: 0.75 },
                    }}
                  >
                    {displaySetpoint != null ? `${displaySetpoint}°` : '—'}
                  </Typography>
                )}
              </Box>
            </Box>
            <RoundControl
              ariaLabel={t('samsung.modal.setpointUp')}
              disabled={setpointDisabled}
              onClick={() => {
                if (editingSetpoint) cancelEditSetpoint();
                void run('setpoint', (displaySetpoint ?? 22) + 1);
              }}
              size={37}
            >
              <AddIcon sx={{ fontSize: 22 }} />
            </RoundControl>
          </Box>
        </Box>
      </Box>

      {/* Modos */}
      <Box>
        <SectionLabel>{t('samsung.modal.mode')}</SectionLabel>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
          gap: 0.65,
        }}>
          {AC_MODES_UI.map((m) => {
            const meta = HVAC_META[m];
            const selected = hvacUi === m;
            return (
              <ChoiceChip
                key={m}
                selected={selected}
                disabled={modeFanDisabled}
                color={meta.color}
                onClick={() => run('hvac_mode', stHvacModeValue(m))}
                minHeight={58}
              >
                <Box sx={{ color: selected ? meta.color : 'rgba(255,255,255,0.55)', display: 'flex' }}>
                  {meta.icon}
                </Box>
                <Typography sx={{ fontSize: 10.5, fontWeight: 700, lineHeight: 1.1 }}>
                  {t(`samsung.hvac.${m}`)}
                </Typography>
              </ChoiceChip>
            );
          })}
        </Box>
      </Box>

      {/* Ventilador */}
      <Box>
        <SectionLabel>
          {hvacUi === 'dry' ? t('samsung.modal.fanDryHint') : t('samsung.modal.fan')}
        </SectionLabel>
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: `repeat(${fanModes.length}, minmax(0, 1fr))`,
          gap: 0.65,
        }}>
          {fanModes.map((m) => {
            const selected = fanValue === m;
            return (
              <ChoiceChip
                key={m}
                selected={selected}
                disabled={modeFanDisabled || (hvacUi === 'dry' && m !== 'auto')}
                color={modeColor}
                onClick={() => {
                  if (hvacUi === 'dry' && m !== 'auto') return;
                  void run('fan_mode', m);
                }}
                minHeight={42}
              >
                <Typography sx={{ fontSize: 12, fontWeight: 700 }}>
                  {t(`samsung.fan.${m}`)}
                </Typography>
              </ChoiceChip>
            );
          })}
        </Box>
      </Box>

      {/* gap de sección (1.25) + margen previo (0.75) + 15px pedidos */}
      <Box sx={{ mt: (theme) => `calc(${theme.spacing(0.75)} + 15px)` }}>
        <SamsungActivityChart deviceId={live.id} showTemperature />
      </Box>
    </Box>
  );

  const bumpVolume = (delta: number) => {
    if (!can) {
      setPermissionSnackbar({ open: true, message: t('permissions.noControlDevices') });
      return;
    }
    const cur = displayVolume;
    const next = Math.max(0, Math.min(100, cur + delta));
    if (next !== cur) void run('volume', next);
  };

  const tvBody = (
    <>
      {/* Remoto + volumen: crecen con el espacio liberado del gráfico */}
      <Box sx={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
        gap: 0.5,
        px: 0.25,
      }}>
        <Box sx={{
          flex: '1 1 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 1.1,
          minWidth: 0,
        }}>
          <Box sx={{
            width: 228,
            height: 228,
            maxWidth: '100%',
            borderRadius: '50%',
            position: 'relative',
            bgcolor: 'rgba(255,255,255,0.03)',
            border: `1px solid ${SURFACE_BORDER}`,
            boxShadow: 'inset 0 0 40px rgba(0,0,0,0.45)',
            backgroundImage: 'radial-gradient(circle at 50% 50%, rgba(35,145,255,0.08) 0%, transparent 55%)',
          }}>
            {([
              { key: 'UP', top: 12, left: '50%', transform: 'translateX(-50%)', icon: <KeyboardArrowUpIcon /> },
              { key: 'DOWN', bottom: 12, left: '50%', transform: 'translateX(-50%)', icon: <KeyboardArrowDownIcon /> },
              { key: 'LEFT', left: 12, top: '50%', transform: 'translateY(-50%)', icon: <KeyboardArrowLeftIcon /> },
              { key: 'RIGHT', right: 12, top: '50%', transform: 'translateY(-50%)', icon: <KeyboardArrowRightIcon /> },
            ] as const).map((btn) => {
              const active = pressedKey === btn.key;
              return (
                <IconButton
                  key={btn.key}
                  disabled={false}
                  onClick={() => runKey(btn.key)}
                  aria-label={btn.key}
                  sx={{
                    position: 'absolute',
                    ...('top' in btn ? { top: btn.top } : {}),
                    ...('bottom' in btn ? { bottom: btn.bottom } : {}),
                    ...('left' in btn ? { left: btn.left } : {}),
                    ...('right' in btn ? { right: btn.right } : {}),
                    transform: `${btn.transform}${active ? ' scale(0.9)' : ''}`,
                    width: 52,
                    height: 52,
                    color: '#fff',
                    bgcolor: active ? 'rgba(35,145,255,0.28)' : 'rgba(255,255,255,0.06)',
                    border: active ? `1px solid ${ACCENT}` : `1px solid ${SURFACE_BORDER}`,
                    transition: PRESS_TRANSITION,
                    WebkitTapHighlightColor: 'transparent',
                    '&:hover:not(.Mui-disabled)': {
                      bgcolor: 'rgba(35,145,255,0.16)',
                      borderColor: ACCENT,
                    },
                    '&:active:not(.Mui-disabled)': {
                      transform: `${btn.transform} scale(0.88)`,
                    },
                    '&.Mui-disabled': { color: '#555', borderColor: '#2a2a2a' },
                  }}
                >
                  {btn.icon}
                </IconButton>
              );
            })}

            <IconButton
              disabled={false}
              onClick={() => runKey('OK')}
              aria-label={t('samsung.modal.ok')}
              sx={{
                position: 'absolute',
                top: '50%',
                left: '50%',
                width: 66,
                height: 66,
                transform: `translate(-50%, -50%)${pressedKey === 'OK' ? ' scale(0.92)' : ''}`,
                color: '#fff',
                fontWeight: 800,
                fontSize: 14,
                letterSpacing: 0.5,
                bgcolor: pressedKey === 'OK' ? 'rgba(35,145,255,0.45)' : 'rgba(35,145,255,0.2)',
                border: `1.5px solid ${ACCENT}`,
                boxShadow: `0 0 24px ${ACCENT}44`,
                transition: PRESS_TRANSITION,
                WebkitTapHighlightColor: 'transparent',
                '&:hover:not(.Mui-disabled)': {
                  bgcolor: 'rgba(35,145,255,0.32)',
                },
                '&:active:not(.Mui-disabled)': {
                  transform: 'translate(-50%, -50%) scale(0.9)',
                },
                '&.Mui-disabled': {
                  color: '#666',
                  borderColor: '#355a7a',
                  bgcolor: 'rgba(35,145,255,0.08)',
                  boxShadow: 'none',
                },
              }}
            >
              {t('samsung.modal.ok')}
            </IconButton>
          </Box>

          <Box sx={{ display: 'flex', gap: 0.85, flexWrap: 'wrap', justifyContent: 'center' }}>
            <ChoiceChip
              selected={pressedKey === 'CH_DOWN'}
              disabled={false}
              onClick={() => runKey('CH_DOWN')}
              minHeight={40}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.75 }}>
                {t('samsung.modal.channelDown', { defaultValue: 'CH −' })}
              </Typography>
            </ChoiceChip>
            <ChoiceChip
              selected={pressedKey === 'CH_UP'}
              disabled={false}
              onClick={() => runKey('CH_UP')}
              minHeight={40}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.75 }}>
                {t('samsung.modal.channelUp', { defaultValue: 'CH +' })}
              </Typography>
            </ChoiceChip>
            <ChoiceChip
              selected={pressedKey === 'BACK'}
              disabled={false}
              onClick={() => runKey('BACK')}
              minHeight={40}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
                <UndoIcon sx={{ fontSize: 15 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{t('samsung.modal.back')}</Typography>
              </Box>
            </ChoiceChip>
            <ChoiceChip
              selected={pressedKey === 'HOME'}
              disabled={false}
              onClick={() => runKey('HOME')}
              minHeight={40}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, px: 1 }}>
                <HomeOutlinedIcon sx={{ fontSize: 15 }} />
                <Typography sx={{ fontSize: 11, fontWeight: 700 }}>{t('samsung.modal.home')}</Typography>
              </Box>
            </ChoiceChip>
            <ChoiceChip
              selected={pressedKey === 'MENU'}
              disabled={false}
              onClick={() => runKey('MENU')}
              minHeight={40}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.75 }}>
                {t('samsung.modal.menu', { defaultValue: 'Menú' })}
              </Typography>
            </ChoiceChip>
            <ChoiceChip
              selected={pressedKey === 'SOURCE'}
              disabled={false}
              onClick={() => runKey('SOURCE')}
              minHeight={40}
            >
              <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.75 }}>
                {t('samsung.modal.source', { defaultValue: 'Fuente' })}
              </Typography>
            </ChoiceChip>
          </Box>
        </Box>

        {/* Volumen más al centro: pegado al D-pad, con ± a la derecha del slider */}
        <Box sx={{
          width: 96,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          alignSelf: 'center',
          borderRadius: '16px',
          px: 0.65,
          pt: 1,
          pb: 1.25,
          border: `1px solid ${SURFACE_BORDER}`,
          background: `
            radial-gradient(90% 80% at 50% 0%, ${ACCENT}20 0%, transparent 55%),
            linear-gradient(180deg, rgba(255,255,255,0.04) 0%, rgba(0,0,0,0.18) 100%)
          `,
        }}>
          <Typography sx={{
            fontSize: 10,
            color: 'rgba(255,255,255,0.45)',
            fontWeight: 600,
            letterSpacing: '0.06em',
            textTransform: 'uppercase',
            mb: 0.35,
          }}>
            {t('samsung.modal.volume')}
          </Typography>
          <TextField
            disabled={false}
            value={volumeDraft}
            onFocus={() => {
              setEditingVolume(true);
              setVolumeDraft('');
            }}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '').slice(0, 3);
              setVolumeDraft(v);
            }}
            onBlur={() => {
              setEditingVolume(false);
              if (volumeDraft === '') {
                setVolumeDraft(String(displayVolume));
                return;
              }
              const n = Math.max(0, Math.min(100, parseInt(volumeDraft, 10) || 0));
              setVolumeDraft(String(n));
              if (n !== displayVolume) void run('volume', n);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                (e.target as HTMLInputElement).blur();
              }
            }}
            variant="standard"
            placeholder="—"
            inputProps={{
              inputMode: 'numeric',
              style: {
                textAlign: 'center',
                fontSize: 28,
                fontWeight: 750,
                color: live.mute ? 'rgba(255,255,255,0.35)' : '#fff',
                padding: 0,
              },
            }}
            sx={{
              width: '100%',
              mb: 0.5,
              '& .MuiInput-underline:before': { borderBottomColor: '#444' },
              '& .MuiInput-underline:after': { borderBottomColor: ACCENT },
              '& .MuiInputBase-input::placeholder': {
                color: 'rgba(255,255,255,0.35)',
                opacity: 1,
              },
            }}
          />
          <IconButton
            disabled={false}
            aria-label={live.mute ? t('samsung.modal.unmute') : t('samsung.modal.mute')}
            onClick={() => run('mute', !live.mute)}
            sx={{
              color: live.mute ? ACCENT : '#fff',
              mb: 0.5,
              p: 0.5,
            }}
          >
            {live.mute ? <VolumeOffIcon fontSize="small" /> : <VolumeUpIcon fontSize="small" />}
          </IconButton>
          <Box sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'stretch',
            justifyContent: 'center',
            gap: 0.35,
            py: 0.25,
            minHeight: 168,
            width: '100%',
          }}>
            <Box sx={{ flex: 1, display: 'flex', justifyContent: 'center', minWidth: 0 }}>
              <Slider
                orientation="vertical"
                disabled={false}
                value={displayVolume}
                min={0}
                max={100}
                onChange={(_, v) => {
                  const n = v as number;
                  setLocalVolume(n);
                  setVolumeDraft(String(n));
                }}
                onChangeCommitted={(_, v) => run('volume', v)}
                sx={{
                  color: ACCENT,
                  height: '100%',
                  '& .MuiSlider-rail': { opacity: 0.25, bgcolor: '#fff' },
                  '& .MuiSlider-track': {
                    border: 'none',
                    background: `linear-gradient(180deg, ${ACCENT}, ${ACCENT}aa)`,
                  },
                  '& .MuiSlider-thumb': {
                    width: 16,
                    height: 16,
                    bgcolor: '#fff',
                    boxShadow: `0 0 0 3px ${ACCENT}33`,
                    '&:before': { display: 'none' },
                  },
                }}
              />
            </Box>
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'space-between',
              flexShrink: 0,
              py: 0,
            }}>
              <IconButton
                disabled={displayVolume >= 100}
                aria-label={t('samsung.modal.volumeUp')}
                onClick={() => bumpVolume(1)}
                sx={{
                  color: '#fff',
                  p: 0.35,
                  border: `1px solid ${SURFACE_BORDER}`,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: '10px',
                  transition: PRESS_TRANSITION,
                  '&:hover:not(.Mui-disabled)': {
                    bgcolor: 'rgba(35,145,255,0.18)',
                    borderColor: ACCENT,
                  },
                  '&:active:not(.Mui-disabled)': { transform: 'scale(0.9)' },
                  '&.Mui-disabled': { color: '#555', borderColor: '#2a2a2a' },
                }}
              >
                <AddIcon sx={{ fontSize: 18 }} />
              </IconButton>
              <IconButton
                disabled={displayVolume <= 0}
                aria-label={t('samsung.modal.volumeDown')}
                onClick={() => bumpVolume(-1)}
                sx={{
                  color: '#fff',
                  p: 0.35,
                  border: `1px solid ${SURFACE_BORDER}`,
                  bgcolor: 'rgba(255,255,255,0.05)',
                  borderRadius: '10px',
                  transition: PRESS_TRANSITION,
                  '&:hover:not(.Mui-disabled)': {
                    bgcolor: 'rgba(35,145,255,0.18)',
                    borderColor: ACCENT,
                  },
                  '&:active:not(.Mui-disabled)': { transform: 'scale(0.9)' },
                  '&.Mui-disabled': { color: '#555', borderColor: '#2a2a2a' },
                }}
              >
                <RemoveIcon sx={{ fontSize: 18 }} />
              </IconButton>
            </Box>
          </Box>
          {live.input_source && (
            <Typography sx={{
              fontSize: 9,
              color: 'rgba(255,255,255,0.55)',
              fontWeight: 600,
              textAlign: 'center',
              mt: 0.5,
              maxWidth: '100%',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}>
              {live.input_source}
            </Typography>
          )}
        </Box>
      </Box>
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.6, justifyContent: 'center', mt: 0.8 }}>
          {[
            ['Netflix', 'netflix'],
            ['YouTube', 'youtube'],
            ['Prime', 'prime'],
            ['Disney+', 'disney'],
            ['Spotify', 'spotify'],
          ].map(([label, id]) => (
            <ChoiceChip key={id} selected={false} disabled={false} onClick={() => run('launch_app', id)} minHeight={34}>
              <Typography sx={{ fontSize: 11, fontWeight: 700, px: 0.8 }}>{label}</Typography>
            </ChoiceChip>
          ))}
        </Box>

      <Box sx={{ mt: 0.75, mb: 0.25 }}>
        <SamsungActivityChart
          deviceId={live.id}
          showTemperature={false}
          chartHeight={105}
        />
      </Box>
    </>
  );

  const body = (
    <Box sx={{
      flex: 1,
      minHeight: 0,
      overflowY: 'auto',
      px: 1.75,
      py: 1.1,
      display: 'flex',
      flexDirection: 'column',
      gap: 1.25,
      background: 'radial-gradient(120% 60% at 50% -10%, rgba(35,145,255,0.08) 0%, transparent 50%)',
    }}>
      {sleepyWarning && (
        <Typography sx={{
          fontSize: 11,
          color: '#e5a54b',
          textAlign: 'center',
          lineHeight: 1.35,
          px: 0.5,
          mt: -0.25,
        }}>
          {sleepyWarning}
        </Typography>
      )}
      {powerRow}
      {live.device_kind === 'ac' ? acBody : tvBody}
      {live.missing_from_cloud && (
        <Typography sx={{ fontSize: 11, color: '#e5a54b', textAlign: 'center' }}>
          {t('samsung.modal.missingFromCloud')}
        </Typography>
      )}
      {error && (
        <Box sx={{
          px: 1.25,
          py: 0.85,
          borderRadius: '12px',
          bgcolor: 'rgba(229,115,115,0.12)',
          border: '1px solid rgba(229,115,115,0.35)',
        }}>
          <Typography sx={{ color: '#ef9a9a', fontSize: 12, textAlign: 'center', lineHeight: 1.35 }}>
            {error}
          </Typography>
        </Box>
      )}
    </Box>
  );

  const permissionSnack = (
    <Snackbar
      open={permissionSnackbar.open}
      autoHideDuration={4000}
      onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
      {...getDemoRestrictionSnackbarProps()}
    >
      <Alert
        onClose={() => setPermissionSnackbar({ ...permissionSnackbar, open: false })}
        severity="error"
        sx={{ width: '100%' }}
      >
        {permissionSnackbar.message}
      </Alert>
    </Snackbar>
  );

  if (isHorizontal) {
    return (
      <Box
        onPointerDownCapture={noteActivity}
        onWheelCapture={noteActivity}
        onKeyDownCapture={noteActivity}
        onTouchStart={(e) => { noteActivity(); handleTouchStart(e); }}
        onTouchEnd={handleTouchEnd}
        sx={{ width: '100%', height: '100%', backgroundColor: '#000', color: 'white', display: 'flex', flexDirection: 'column' }}
      >
        <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
          <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Paper sx={{
              width: '100%', height: '100%', backgroundColor: '#000', color: 'white',
              display: 'flex', flexDirection: 'column', borderRadius: 0, boxShadow: 'none',
            }}>
              {header}
              {body}
            </Paper>
          </Box>
        </Slide>
        {permissionSnack}
      </Box>
    );
  }

  return (
    <Box
      onPointerDownCapture={noteActivity}
      onWheelCapture={noteActivity}
      onKeyDownCapture={noteActivity}
      onTouchStart={(e) => { noteActivity(); handleTouchStart(e); }}
      sx={{
        position: 'fixed', inset: 0, zIndex: 1360,
        display: open ? 'flex' : 'none',
        alignItems: 'center', justifyContent: 'center',
      }}
    >
      <Box onClick={requestClose} sx={{ position: 'absolute', inset: 0, backgroundColor: 'transparent' }} />
      <Slide direction="left" in={open && !isClosing} timeout={300} appear mountOnEnter unmountOnExit>
        <Paper
          onTouchEnd={handleTouchEnd}
          sx={{
            position: 'fixed', bottom: SHC_BOTTOM_NAV_CLEARANCE, top: '40px', left: '1%', right: '1%', width: '98%',
            backgroundColor: '#000', color: 'white', zIndex: 1400,
            display: 'flex', flexDirection: 'column', overflow: 'hidden',
            borderRadius: '16px 16px 0 0',
            boxShadow: '-4px 0 20px rgba(0,0,0,0.5)',
            border: '1px solid #444', borderTop: '2px solid #2391FF',
          }}
        >
          {header}
          {body}
        </Paper>
      </Slide>
      {permissionSnack}
    </Box>
  );
};

export default SamsungDeviceModal;
